import { useMemo } from 'react';
import {
  computeSpansLayers,
  fmt,
  freshLens,
  type Perspective,
  type SeparationState,
  type SpansLayers,
} from '@integrate/core';
import { StatTile } from '../ui/StatTile.js';

/*
  Spans and layers — the first structural diagnostic of any org design, drawn at last.

  The engine (`computeSpansLayers`) has been in core since P2, parity-tested against the frozen app,
  and NO pane rendered it: the standard first question about a target design — "how deep, how wide,
  who is overloaded" — was computed and invisible. The frozen app kept this in a modal of its own
  behind a toolbar button (orgchart.html:8811); here it is an analytics tab, because it is read
  beside the other analytics and never edits anything.

  THE PERSPECTIVE IS THE READER'S, not the chart's — the same decision LevelPane documents. The
  engine's own contract is "the population on screen", so it takes a whole lens; this pane hands it
  `freshLens()` with only the perspective swapped, which is the neutral Day 1 view of each org. The
  chart's filters and pivots deliberately do NOT reach it: a spans figure that silently narrowed with
  a hidden filter row would describe a different organisation than its caption claims.

  ALL THREE PERSPECTIVES ARE COMPUTED — the comparison table is the frozen modal's centrepiece and
  the reason this pane earns its place: RemainCo after the carve-out is where delayering questions
  actually land, and the comparison is what shows a cut org keeping its old management stack.

  The 3 / 8 span thresholds are the engine's, ported from the frozen app; they are stated in the
  captions rather than configurable, which is honest until IDEAS.md #4 (guardrails) makes them
  assumptions.
*/

const PERSPECTIVES: { key: Perspective; label: string }[] = [
  { key: 'as-is', label: 'As-is' },
  { key: 'remain', label: 'RemainCo' },
  { key: 'carve-out', label: 'Carve-out' },
];

/** Histogram over [bucket label, count] pairs, in the app's own `.histo` idiom, with a table twin. */
function Histo({
  data,
  label,
  testId,
}: {
  data: [string, number][];
  label: string;
  testId: string;
}): JSX.Element {
  if (!data.length) {
    return (
      <p className="muted" data-testid={`${testId}-empty`}>
        No data.
      </p>
    );
  }
  const max = Math.max(...data.map(([, n]) => n));
  return (
    <>
      <div className="histo" data-testid={testId} data-max={max}>
        {data.map(([k, n]) => (
          <div
            className="bar"
            key={k}
            data-band={k}
            data-n={n}
            style={{ height: `${((n / max) * 100).toFixed(1)}%` }}
            title={`${label} ${k}: ${fmt(n)}`}
          >
            <div className="val">{n || ''}</div>
            <div className="lbl">{k}</div>
          </div>
        ))}
      </div>
      <details className="chart-table">
        <summary>{label} distribution as a table</summary>
        <table className="data-table">
          <thead>
            <tr>
              <th>{label}</th>
              <th className="num">Count</th>
            </tr>
          </thead>
          <tbody>
            {data.map(([k, n]) => (
              <tr key={k}>
                <td>{k}</td>
                <td className="num">{fmt(n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </>
  );
}

export function SpansLayersPane({
  state,
  perspective,
  onPerspective,
}: {
  state: SeparationState;
  /** Owned by the modal, like LevelPane's: pane-local state dies with a tab switch. */
  perspective: Perspective;
  onPerspective(next: Perspective): void;
}): JSX.Element {
  // All three at once: the comparison table needs them, and one pass over a design is cheap enough
  // that computing the current one separately would only create a second code path to keep equal.
  const stats = useMemo(() => {
    const out = {} as Record<Perspective, SpansLayers>;
    for (const p of PERSPECTIVES) {
      out[p.key] = computeSpansLayers(state, { ...freshLens(), perspective: p.key });
    }
    return out;
  }, [state]);

  const cur = stats[perspective];
  const label = PERSPECTIVES.find((p) => p.key === perspective)!.label;

  return (
    <div data-testid="spans-layers-pane" data-perspective={perspective}>
      <p className="muted small" style={{ marginTop: 0 }}>
        Layers count depth from the organisation root; span is the number of direct reports under a
        node. Designated heads replace their group's node, so they carry its span. Measured on the
        Day 1 view of each perspective, unfiltered.
      </p>

      <div className="row" style={{ alignItems: 'center', gap: 12 }}>
        <span className="muted small">Population</span>
        <div className="tabs" role="group" aria-label="Perspective">
          {PERSPECTIVES.map((p) => (
            <button
              key={p.key}
              className={`toggle-btn${perspective === p.key ? ' active' : ''}`}
              aria-pressed={perspective === p.key}
              data-testid={`spans-perspective-${p.key}`}
              onClick={() => onPerspective(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="stat-grid"
        data-testid="spans-strip"
        data-max-layer={cur.maxLayer}
        data-avg-span={cur.avgSpan}
        data-low={cur.lowSpan.length}
        data-high={cur.highSpan.length}
      >
        <StatTile label="Layers (max depth)" valueTestId="spans-max-layer" value={fmt(cur.maxLayer)} sub={`${label} view`} />
        <StatTile label="Avg span" valueTestId="spans-avg" value={fmt(cur.avgSpan, 1)} sub="across all parents" />
        <StatTile label="Low-span outliers" valueTestId="spans-low" value={fmt(cur.lowSpan.length)} sub="< 3 direct reports" />
        <StatTile label="High-span outliers" valueTestId="spans-high" value={fmt(cur.highSpan.length)} sub="> 8 direct reports" />
      </div>

      <h4>Layer distribution (depth from root)</h4>
      <Histo data={cur.layerHisto} label="Layer" testId="spans-layer-histo" />

      <h4>Span of control distribution</h4>
      <Histo data={cur.spanHisto} label="Span" testId="spans-span-histo" />

      {cur.breakdownLabel && cur.byFunction.length > 0 && (
        <>
          <h4>By {cur.breakdownLabel}</h4>
          <table className="data-table" data-testid="spans-by-function">
            <thead>
              <tr>
                <th>{cur.breakdownLabel}</th>
                <th className="num">Avg layers</th>
                <th className="num">Max span</th>
                <th className="num">Min span</th>
                <th className="num">Mgrs &lt; 3</th>
                <th className="num">Mgrs &gt; 8</th>
              </tr>
            </thead>
            <tbody>
              {cur.byFunction.map((r) => (
                <tr key={r.name}>
                  <td>{r.name}</td>
                  <td className="num">{fmt(r.avgLayer, 1)}</td>
                  <td className="num">{fmt(r.maxSpan)}</td>
                  <td className="num">{fmt(r.minSpan)}</td>
                  <td className="num">{fmt(r.low)}</td>
                  <td className="num">{fmt(r.high)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h4>Perspective comparison</h4>
      <table className="data-table" data-testid="spans-comparison">
        <thead>
          <tr>
            <th>Metric</th>
            {PERSPECTIVES.map((p) => (
              <th className="num" key={p.key}>
                {p.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(
            [
              ['Headcount in view', (s: SpansLayers) => fmt(s.count)],
              ['Layers (max depth)', (s: SpansLayers) => fmt(s.maxLayer)],
              ['Avg span', (s: SpansLayers) => fmt(s.avgSpan, 1)],
              ['Low-span (< 3)', (s: SpansLayers) => fmt(s.lowSpan.length)],
              ['High-span (> 8)', (s: SpansLayers) => fmt(s.highSpan.length)],
            ] as const
          ).map(([name, read]) => (
            <tr key={name}>
              <td>{name}</td>
              {PERSPECTIVES.map((p) => (
                <td className="num" key={p.key}>
                  {read(stats[p.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {cur.highSpan.length > 0 && (
        <>
          <h4>High-span managers (&gt; 8 direct reports) — {label}</h4>
          <table className="data-table" data-testid="spans-high-table">
            <thead>
              <tr>
                <th>Manager / group</th>
                <th>{cur.breakdownLabel}</th>
                <th className="num">Span</th>
              </tr>
            </thead>
            <tbody>
              {/* The frozen modal's cap, and stated rather than silent. */}
              {cur.highSpan.slice(0, 20).map((o) => (
                <tr key={`${o.name}-${o.levelValue}`}>
                  <td>{o.name}</td>
                  <td>{o.levelValue}</td>
                  <td className="num">{fmt(o.span)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {cur.highSpan.length > 20 && (
            <p className="muted small" data-testid="spans-high-truncated">
              Showing the first 20 of {fmt(cur.highSpan.length)}.
            </p>
          )}
        </>
      )}
    </div>
  );
}
